/**
 * When a card image will not load, ask for it through our own relay.
 *
 * Card art lives on other people's hosts, and on one afternoon two of them
 * failed at once: TCGdex's asset CDN answered 404 for everything, and Collectr
 * began forbidding browsers from painting its images anywhere but its own
 * site. Every card in the app went blank, on screens that had shown them an
 * hour before.
 *
 * This listens for image load failures at the document, ahead of any
 * component, and swaps the failed URL for the card-art function, which serves
 * our stored copy or fetches the image from the original or a fallback host
 * and stores it. The first failure never reaches the component's own onError,
 * so no placeholder is drawn while the retry is in flight. If the relay fails
 * too, the second error passes through and the component does what it always
 * did.
 *
 * Direct loads still happen first, so a healthy host costs nothing extra.
 */

const RELAY_HOSTS = ['assets.tcgdex.net', 'www.getcollectr.com', 'images.pokemontcg.io'];

export function relayUrl(original) {
  const base = process.env.REACT_APP_SUPABASE_URL;
  if (!base) return null;
  return `${base}/functions/v1/card-art?u=${encodeURIComponent(original)}`;
}

export function shouldRelay(src) {
  if (!src || typeof src !== 'string') return false;
  if (src.includes('/functions/v1/card-art')) return false;
  try {
    return RELAY_HOSTS.includes(new URL(src).hostname);
  } catch {
    return false;
  }
}

let installed = false;

export function installArtRelay(doc = typeof document !== 'undefined' ? document : null) {
  if (installed || !doc) return;
  installed = true;
  doc.addEventListener('error', (event) => {
    const el = event.target;
    if (!el || el.tagName !== 'IMG') return;
    const src = el.currentSrc || el.src;
    if (!shouldRelay(src)) return;
    const next = relayUrl(src);
    if (!next) return;
    // Ours to handle. The component's own onError does not hear this one.
    event.stopPropagation();
    el.src = next;
  }, true);
}

export default installArtRelay;
